/**
 * /firms — Audience-targeted landing for law firms with trust departments.
 *
 * Replaces the Phase 9 ComingSoonShell stub per Phase 3 Task 3.7 of the
 * 2026-05-16 implementation plan.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { MarketingLayout } from '../components/layouts/marketing-layout';

export const Route = createFileRoute('/firms')({
  component: FirmsPage,
});

function FirmsPage() {
  useEffect(() => {
    document.title = 'For fiduciary firms · Elreign: trust administration that runs a practice';
    const meta = document.querySelector('meta[name="description"]') ?? (() => {
      const m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute(
      'content',
      'A platform built for South African law firms, trust companies, and fiduciary practices. White-label tenant, multi-trustee signing, FICA pipeline, Master ICMS lodgements, and an immutable audit chain.',
    );
  }, []);

  return (
    <MarketingLayout>
      <div style={{ maxWidth: 920, margin: '0 auto', padding: '72px 24px 96px' }}>
        {/* Hero */}
        <header style={{ marginBottom: 64 }}>
          <p className="eyebrow" style={{ marginBottom: 18 }}>For fiduciary firms</p>
          <h1
            className="serif"
            style={{
              fontSize: 'clamp(40px, 6vw, 64px)',
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              lineHeight: 1.1,
              letterSpacing: '-0.025em',
              margin: '0 0 24px',
            }}
          >
            Run forty-seven trusts <em>the way you&apos;d run one.</em>
          </h1>
          <p
            style={{
              fontSize: 19,
              color: 'var(--ink-3)',
              lineHeight: 1.65,
              margin: 0,
              maxWidth: 660,
            }}
          >
            Elreign is built for South African fiduciary practices: law firms with a
            trust department, dedicated trust companies, family offices, and chartered fiduciary
            practitioners. White-label the tenant, charge by the user, keep the record.
          </p>
        </header>

        {/* Benefits */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 36, marginBottom: 64 }}>
          <Benefit
            eyebrow="White-label tenant"
            heading="Your subdomain, your colours, your sender name"
            body="Practice and Enterprise tiers run on a tenant subdomain at firm.elreign.io with your logo, brand palette, and sender name on every notification, resolution, and signing prompt. Beneficiaries and trustees see your firm, not us."
          />
          <Benefit
            eyebrow="FICA pipeline"
            heading="Lodgements queued, tracked, lodged"
            body="The Master ICMS lodgement queue is part of the dashboard. Each trust shows next FICA review date, days outstanding, and the lodgement status. Bulk actions across the trust register let a senior administrator clear a hundred renewals in a morning."
          />
          <Benefit
            eyebrow="Per-client pricing"
            heading="Priced per client, and the rate drops as you grow"
            body="One rate per client per month, with three trusts per client included. For most clients that is a single predictable line. The rate falls at 100 clients and again at 500, so growth lowers your cost per client instead of raising it. Trusts beyond the included three are billed at your tier's overage rate, itemised on the same invoice. Two lines, nothing else."
          />
          <Benefit
            eyebrow="Audit chain"
            heading="Holds in court: every signature, every BO change"
            body="When the question is 'who authorised this distribution in 2017?' the answer is on the chain: who signed, what they signed, the fingerprint they matched against, the device they used, the time to the second. Recoverable on demand, verifiable independently, retained for five years minimum."
          />
          <Benefit
            eyebrow="Integration"
            heading="Sits next to your practice management system"
            body="The platform is the system of record for the trust lifecycle, not the firm. Time recording, billing, conflicts checking, and matter management remain in your existing PMS. Two-way integration is available for Lexis Affinity, AJS, and CMS, with custom connectors built on request."
          />
        </section>

        {/* CTA */}
        <section
          style={{
            padding: '40px 36px',
            background: 'var(--primary-700)',
            color: '#FAF7F0',
            borderRadius: 'var(--r-xl)',
          }}
        >
          <p className="eyebrow on-dark" style={{ marginBottom: 16 }}>
            Talk to us
          </p>
          <h2
            className="serif"
            style={{
              fontSize: 32,
              fontStyle: 'italic',
              fontWeight: 300,
              color: '#FAF7F0',
              margin: '0 0 14px',
              letterSpacing: '-0.015em',
              lineHeight: 1.2,
            }}
          >
            See the platform with your trusts on it.
          </h2>
          <p
            style={{
              fontSize: 16,
              color: 'rgba(250,247,240,0.85)',
              lineHeight: 1.6,
              margin: '0 0 24px',
              maxWidth: 540,
            }}
          >
            Send us your trust register format and we will set up a tenant with anonymised
            structure mirrored from your real data. You see exactly what your administrators would
            see, on day one of live use.
          </p>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Link
              to="/auth/signup"
              style={{
                background: 'var(--accent-400)',
                color: 'var(--primary-900)',
                padding: '12px 24px',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Get started
            </Link>
            <Link
              to="/contact"
              style={{
                fontSize: 15,
                color: 'var(--accent-300)',
                textDecoration: 'none',
                borderBottom: '1px solid rgba(193,154,91,0.4)',
                paddingBottom: 1,
                alignSelf: 'center',
              }}
            >
              Book a tenant walkthrough
            </Link>
            <Link
              to="/pricing"
              style={{
                fontSize: 15,
                color: 'var(--accent-300)',
                textDecoration: 'none',
                borderBottom: '1px solid rgba(193,154,91,0.4)',
                paddingBottom: 1,
                alignSelf: 'center',
              }}
            >
              See pricing
            </Link>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
}

interface BenefitProps {
  eyebrow: string;
  heading: string;
  body: string;
}

function Benefit({ eyebrow, heading, body }: BenefitProps) {
  return (
    <article
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(140px, 200px) 1fr',
        gap: 28,
        paddingTop: 28,
        borderTop: '1px solid var(--hairline)',
      }}
    >
      <p className="eyebrow" style={{ margin: 0 }}>{eyebrow}</p>
      <div>
        <h3
          className="serif"
          style={{
            fontSize: 24,
            fontStyle: 'italic',
            fontWeight: 300,
            color: 'var(--ink)',
            margin: '0 0 12px',
            letterSpacing: '-0.01em',
            lineHeight: 1.25,
          }}
        >
          {heading}
        </h3>
        <p style={{ fontSize: 17, color: 'var(--ink-2)', lineHeight: 1.65, margin: 0 }}>{body}</p>
      </div>
    </article>
  );
}
